// Finanzplanung — Haushaltsanalyse-Engine (Portierung „Mandanten-Log II").
//
// Pure Functions ohne DB/IO: der Server speichert nur die Eingaben
// (finance_budget_items), Frontend UND API rechnen mit derselben Engine.
// Beträge durchgehend als Integer-Cents (Repo-Konvention).
//
// Fachlogik aus dem Original-Workbook (Numbers-Export, 2026-07-18):
//  - 4 Ausgaben-Kategorien: Fest (rot) / Flüssig (gelb) / Sicherheit (blau) /
//    Vermögensaufbau (grün), plus Einnahmen.
//  - „E-A" (Übrig) = Summe Einnahmen − Summe Ausgaben (inkl. Vermögensaufbau).
//  - „VA-Bilanz" = E-A + Vermögensaufbau — das ist die Sparleistung, aus der
//    die Finanznote entsteht.
//  - Finanznote: deutsche Schulnote 1+ … 6 über den Sparanteil
//    (VA-Bilanz / Einnahmen) in 3-%-Stufen; 30 % = Standard-Zielsparquote.

export const FINANCE_BUDGET_SECTIONS = [
  'income',
  'fixed',
  'variable',
  'insurance',
  'wealth',
] as const

export type FinanceBudgetSection = (typeof FINANCE_BUDGET_SECTIONS)[number]

/** Server-Cap pro Monatsposten: 1 Mio € — weit über jedem realen Wert, aber
 *  sicher unter dem int4-Limit (2^31−1 Cents ≈ 21,4 Mio €). Geteilt zwischen
 *  API-Zod und Client-Validierung, damit beide dieselbe Grenze sprechen. */
export const FINANCE_MAX_MONTHLY_CENTS = 100_000_000

export const FINANCE_MAX_LABEL_LENGTH = 120

export interface FinanceBudgetItemLike {
  /** Bewusst weit typisiert (string) — DB-Rows liefern text-Spalten. */
  section: string
  monthlyCents: number
}

export interface FinanceBudgetTotals {
  incomeCents: number
  fixedCents: number
  variableCents: number
  insuranceCents: number
  /** Sparen + Investieren (Kategorie „Vermögensaufbau"). */
  wealthCents: number
  /** Summe der Ausgaben = fixed + variable + insurance + wealth (wie Original). */
  expenseCents: number
  /** „E-A" / „Übrig" = Einnahmen − Ausgaben. Kann negativ sein. */
  surplusCents: number
  /** „VA-Bilanz" = E-A + Vermögensaufbau — die effektive Sparleistung. */
  vaBilanzCents: number
}

export function computeFinanceBudget(items: FinanceBudgetItemLike[]): FinanceBudgetTotals {
  let income = 0
  let fixed = 0
  let variable = 0
  let insurance = 0
  let wealth = 0
  for (const item of items) {
    const cents = Number.isFinite(item.monthlyCents) ? item.monthlyCents : 0
    switch (item.section) {
      case 'income':
        income += cents
        break
      case 'fixed':
        fixed += cents
        break
      case 'variable':
        variable += cents
        break
      case 'insurance':
        insurance += cents
        break
      case 'wealth':
        wealth += cents
        break
      default:
        // Unbekannte Sektion (Daten-Drift) — bewusst ignorieren statt werfen.
        break
    }
  }
  const expense = fixed + variable + insurance + wealth
  const surplus = income - expense
  return {
    incomeCents: income,
    fixedCents: fixed,
    variableCents: variable,
    insuranceCents: insurance,
    wealthCents: wealth,
    expenseCents: expense,
    surplusCents: surplus,
    vaBilanzCents: surplus + wealth,
  }
}

export interface FinanznoteStep {
  grade: string
  /** Untergrenze des Sparanteils in Prozent (inklusive). */
  minPercent: number
}

/** E-A-Notenskala aus dem Original: 3-%-Stufen von 45 % (1+) bis 0 % (6). */
export const FINANZNOTE_SCALE: readonly FinanznoteStep[] = [
  { grade: '1+', minPercent: 45 },
  { grade: '1', minPercent: 42 },
  { grade: '1-', minPercent: 39 },
  { grade: '2+', minPercent: 36 },
  { grade: '2', minPercent: 33 },
  { grade: '2-', minPercent: 30 },
  { grade: '3+', minPercent: 27 },
  { grade: '3', minPercent: 24 },
  { grade: '3-', minPercent: 21 },
  { grade: '4+', minPercent: 18 },
  { grade: '4', minPercent: 15 },
  { grade: '4-', minPercent: 12 },
  { grade: '5+', minPercent: 9 },
  { grade: '5', minPercent: 6 },
  { grade: '5-', minPercent: 3 },
  { grade: '6', minPercent: 0 },
] as const

/** Standard-Zielsparquote (im Original grün markierte Stufe „2-"). */
export const FINANZNOTE_TARGET_PERCENT = 30

export interface Finanznote {
  grade: string
  /** Sparanteil (VA-Bilanz / Einnahmen) in Prozent, ungerundet. */
  percent: number
}

/**
 * Finanznote aus den Budget-Summen. `null` ohne Einnahmen — ohne Bezugsgröße
 * gibt es keinen Sparanteil (Anzeige „—"). Negativer Sparanteil ⇒ Note 6.
 */
export function finanznote(totals: FinanceBudgetTotals): Finanznote | null {
  if (totals.incomeCents <= 0) return null
  const percent = (totals.vaBilanzCents / totals.incomeCents) * 100
  const step = FINANZNOTE_SCALE.find((s) => percent >= s.minPercent)
  return { grade: step ? step.grade : '6', percent }
}
